import { get, post, put, del } from './client'

// The queue-distribution settings surface is a LEGACY config API scoped to the
// four original workflows (it reads/writes per-workflow KV configs). It is
// superseded by WorkflowDefinition.distribution in the V2 registry and goes
// away with it — hence the deliberately closed union.
export type QueueWorkflowId = 'confirmation' | 'sales' | 'shipping_confirmation' | 'customer_service'

// ── QueueDistributionConfig types ─────────────────────────────────────────────

export type DistributionMethod   = 'round_robin' | 'fair_split'
// Pull modes are relative to the workflow's batch size (its Distribution
// Strategy). Legacy 'threshold'/'anytime' are normalized to 'topup' by the API.
export type TopUpMode            = 'topup' | 'empty_first' | 'percentage' | 'threshold' | 'anytime'
export type LateJoinMode         = 'full_batch' | 'remaining_share'
export type PostBreachAllocation = 'full_batch' | 'remaining_share' | 'no_allocation'
export type IndividualHoursConflict = 'distribute_at_team_time' | 'distribute_at_individual_time'
export type BreachAction         = 'release_orders' | 'keep_orders'

export type QueueDistributionConfig = {
  preAssignment: {
    enabled:                 boolean
    gracePeriodMinutes:      number
    individualHoursConflict: IndividualHoursConflict
    lateAgentJoinMode:       LateJoinMode
  }
  distribution: {
    method:     DistributionMethod
    batchSize?: number
  }
  topUp: {
    enabled?:           boolean  // false = agents cannot pull at all
    mode:               TopUpMode
    percentage?:        number   // mode = 'percentage': % of batch completed before pull
    threshold?:         number   // legacy — no longer written
    maxPerAgentPerDay?: number
  }
  breakPolicy: {
    maxBreakMinutes: number
    onExceeded:      BreachAction
    onReturn:        PostBreachAllocation
  }
  reentryPolicy: {
    allowed:   boolean
    onReentry: PostBreachAllocation
  }
  // G-13: backlog assignment mode
  backlogAssignmentMode?:   'stay_assigned' | 'return_to_pool'
  // G-14: postponed orders redistribution mode
  postponedAssignmentMode?: 'same_agent' | 'any_agent'
}

// ── Agent credentials ─────────────────────────────────────────────────────────

export type AgentInfo = { name: string }
export type AgentCredentialsList = { agents: AgentInfo[] }

// ── API calls ─────────────────────────────────────────────────────────────────

export function getDistributionConfig(workflowId: QueueWorkflowId): Promise<QueueDistributionConfig> {
  return get(`/settings/queue-distribution?workflow=${workflowId}`)
}

export function saveDistributionConfig(
  workflowId: QueueWorkflowId,
  patch: Partial<QueueDistributionConfig>,
): Promise<QueueDistributionConfig> {
  return post(`/settings/queue-distribution?workflow=${workflowId}`, patch)
}

export function listAgentCredentials(): Promise<AgentCredentialsList> {
  return get('/settings/agent-credentials')
}

export function upsertAgentCredential(name: string, password: string): Promise<{ ok: boolean; name: string }> {
  return put('/settings/agent-credentials', { name, password })
}

export function deleteAgentCredential(name: string): Promise<{ ok: boolean }> {
  return del(`/settings/agent-credentials?name=${encodeURIComponent(name)}`)
}
